import { StickyPosition } from '@/types/table';
import { isStickyLeft, isStickyRight } from './columnLayout';
import {
  convertRemToTableSizeUnits,
  formatRemValue,
  formatTableSizeUnitsAsRem,
  ROW_EXTENSION_COLUMN_WIDTH_REM,
  STICKY_RIGHT_OFFSET_REM,
  TABLE_SIZE_UNITS_PER_REM,
} from './columnLayout.constants';

const ROW_EXTENSION_COLUMN_WIDTH = convertRemToTableSizeUnits(ROW_EXTENSION_COLUMN_WIDTH_REM);

type ColumnDefForOffsetCalculation = {
  size?: number;
  sticky?: boolean | StickyPosition;
};

export type ColumnOffsets = {
  left: (string | undefined)[];
  right: (string | undefined)[];
};

export const buildLeftOffsets = (
  cellsDefs: ColumnDefForOffsetCalculation[],
  hasRowExtension?: boolean,
  verticalColumnColspan?: number,
  isWithVerticalColumn?: boolean,
): (string | undefined)[] => {
  const offsets: (string | undefined)[] = new Array(cellsDefs.length);
  let runningSum = 0;

  for (let i = 0; i < cellsDefs.length; i++) {
    const def = cellsDefs[i];
    if (!isStickyLeft(def.sticky)) {
      offsets[i] = undefined;
    } else if (isWithVerticalColumn && i === 0) {
      const verticalColumnSpan = Math.max(verticalColumnColspan ?? 1, 1);
      offsets[i] = formatTableSizeUnitsAsRem((def.size ?? 0) / verticalColumnSpan);
    } else {
      const base = hasRowExtension ? runningSum + ROW_EXTENSION_COLUMN_WIDTH : runningSum;
      offsets[i] = formatTableSizeUnitsAsRem(base);
    }
    runningSum += def.size ?? 0;
  }

  return offsets;
};

export const buildRightOffsets = (cellsDefs: ColumnDefForOffsetCalculation[]): (string | undefined)[] => {
  const offsets: (string | undefined)[] = new Array(cellsDefs.length);
  let runningSum = 0;

  for (let i = cellsDefs.length - 1; i >= 0; i--) {
    const def = cellsDefs[i];
    if (!isStickyRight(def.sticky)) {
      offsets[i] = undefined;
    } else {
      const isOutermost = runningSum === 0;
      const seamOffsetRem = isOutermost ? STICKY_RIGHT_OFFSET_REM : 0;
      offsets[i] = formatRemValue(runningSum / TABLE_SIZE_UNITS_PER_REM + seamOffsetRem);
      runningSum += def.size ?? 0;
    }
  }

  return offsets;
};

export const buildColumnOffsets = (
  cellsDefs: ColumnDefForOffsetCalculation[],
  hasRowExtension?: boolean,
  verticalColumnColspan?: number,
  isWithVerticalColumn?: boolean,
): ColumnOffsets => ({
  left: buildLeftOffsets(cellsDefs, hasRowExtension, verticalColumnColspan, isWithVerticalColumn),
  right: buildRightOffsets(cellsDefs),
});

export type StickyColumnProps = {
  isStickyColumnLeft: boolean;
  isStickyColumnRight: boolean;
  isLastStickyColumn: boolean;
  isFirstStickyRightColumn: boolean;
  left: string | undefined;
  right: string | undefined;
};

export const getStickyColumnProps = (
  positionedColumnDefs: ColumnDefForOffsetCalculation[],
  columnOffsets: ColumnOffsets,
  index: number,
): StickyColumnProps => {
  const columnDef = positionedColumnDefs[index];
  const isStickyColumnLeft = isStickyLeft(columnDef?.sticky);
  const isStickyColumnRight = isStickyRight(columnDef?.sticky);
  const isNextColumnStickyLeft = isStickyLeft(positionedColumnDefs[index + 1]?.sticky);
  const isLastStickyColumn = isStickyColumnLeft && !isNextColumnStickyLeft;
  const isPrevColumnStickyRight = isStickyRight(positionedColumnDefs[index - 1]?.sticky);
  const isFirstStickyRightColumn = isStickyColumnRight && !isPrevColumnStickyRight;
  const left = isStickyColumnLeft ? columnOffsets.left[index] : undefined;
  const right = isStickyColumnRight ? columnOffsets.right[index] : undefined;

  return { isStickyColumnLeft, isStickyColumnRight, isLastStickyColumn, isFirstStickyRightColumn, left, right };
};
